import { Component } from "react";
import { BACKEND_URL, getToken } from "../lib/api";

// A render error used to black the whole screen out. Now it is caught, reported to the server (so the stack is in
// the API log next time it happens) and the player gets a way back in. Their seat is kept server-side.
export const reportClientError = (what, err, extra = {}) => {
  try {
    const body = JSON.stringify({
      kind: what,
      message: String(err?.message || err || "unknown"),
      stack: String(err?.stack || "").slice(0, 4000),
      url: window.location.href,
      ua: navigator.userAgent,
      ...extra,
    });
    const token = getToken();
    fetch(`${BACKEND_URL}/api/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* reporting must never throw */
  }
};

const RETRY_KEY = "pfp_crash_retry";
const AUTO_MS = 2500;

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null, auto: false };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    reportClientError("render", err, { component_stack: String(info?.componentStack || "").slice(0, 3000) });
    // Straight back to the same seat: the table reloads itself and the socket re-joins. Only once per minute, so a
    // screen that breaks on every render can't put the player in a reload loop.
    const last = Number(sessionStorage.getItem(RETRY_KEY) || 0);
    if (Date.now() - last > 60_000) {
      sessionStorage.setItem(RETRY_KEY, String(Date.now()));
      sessionStorage.setItem("pfp_fast_boot", "1");  // straight back to the felt: skip the splash hold on this reload
      this.setState({ auto: true });
      this.timer = setTimeout(() => window.location.reload(), AUTO_MS);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div data-testid="crash-screen" className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-6">
        <div className="glass rounded-2xl border border-amber-500/30 p-6 max-w-lg flex flex-col gap-3">
          <div className="text-lg font-display text-amber-300">The screen hiccupped</div>
          <p className="text-sm text-slate-300">
            {this.state.auto
              ? "Taking you straight back to your seat — your chips and your place in the hand are safe on the server."
              : "Something in the display broke, not your game. Your seat and your chips are safe on the server — rejoin and you will drop straight back in."}{" "}
            The fault has been reported automatically.
          </p>
          {this.state.auto && <div data-testid="crash-auto" className="text-xs text-emerald-300">Rejoining…</div>}
          <code className="text-[11px] font-mono text-slate-400 break-all">{String(this.state.err?.message || this.state.err)}</code>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              data-testid="crash-reload-button"
              onClick={() => window.location.reload()}
              className="rounded-full px-4 py-2 text-sm font-display bg-emerald-500 hover:bg-emerald-400 text-slate-900"
            >
              Rejoin
            </button>
            <button
              type="button"
              data-testid="crash-lobby-button"
              onClick={() => { window.location.href = "/lobby"; }}
              className="rounded-full px-4 py-2 text-sm font-display border border-slate-600 hover:border-slate-400"
            >
              Back to lobby
            </button>
          </div>
        </div>
      </div>
    );
  }
}
